import React from "react";
import { getStaffingColor, formatPercent, formatNumber } from "./utils";

const TURNOVER_NORM = 5.8;

function GenderKpiCard({ gender }) {
  const womenItem = gender.items?.find((item) => item.key === "female");
  const menItem = gender.items?.find((item) => item.key === "male");
  const unknownItem = gender.items?.find((item) => item.key === "unknown");
  const womenCount = womenItem?.count ?? 0;
  const menCount = menItem?.count ?? 0;
  const unknownCount = unknownItem?.count ?? 0;
  const total = gender.total || womenCount + menCount + unknownCount;

  const rows = [
    {
      key: "female",
      label: "Ж",
      count: womenCount,
      percent: gender.womenPercent,
      tone: "women",
    },
    {
      key: "male",
      label: "М",
      count: menCount,
      percent: gender.menPercent,
      tone: "men",
    },
  ];

  return (
    <article className="hr-dashboard__kpi-card hr-dashboard__kpi-card--gender">
      <p className="hr-dashboard__kpi-title">Гендерное соотношение</p>
      <p className="hr-dashboard__gender-kpi-total">
        {formatNumber(total)} чел.
      </p>
      <div className="hr-dashboard__gender-kpi-bars">
        {rows.map((row) => (
          <div
            key={row.key}
            className={`hr-dashboard__gender-kpi-row hr-dashboard__gender-kpi-row--${row.tone}`}
          >
            <span className="hr-dashboard__gender-kpi-label">{row.label}</span>
            <div className="hr-dashboard__gender-kpi-track">
              <div
                className={`hr-dashboard__gender-kpi-bar hr-dashboard__gender-kpi-bar--${row.tone}`}
                style={{ width: `${row.percent}%` }}
              >
                <span
                  className={`hr-dashboard__gender-kpi-badge hr-dashboard__gender-kpi-badge--${row.tone}`}
                >
                  {row.count}
                </span>
              </div>
            </div>
            <span
              className={`hr-dashboard__gender-kpi-percent hr-dashboard__gender-kpi-percent--${row.tone}`}
            >
              {formatPercent(row.percent).replace("%", "")}%
            </span>
          </div>
        ))}
      </div>
      {unknownCount > 0 && (
        <p className="hr-dashboard__gender-kpi-unknown">
          +{unknownCount} без указания пола
        </p>
      )}
    </article>
  );
}

function KpiCards({ summary, gender }) {
  const staffingTone = getStaffingColor(summary.staffingPercent ?? 0);
  const turnoverNote =
    summary.turnoverAboveNorm != null
      ? `+${formatPercent(summary.turnoverAboveNorm).replace("%", "")}% выше нормы`
      : summary.turnoverPercent > TURNOVER_NORM
        ? `+${(summary.turnoverPercent - TURNOVER_NORM).toFixed(1).replace(".", ",")}% выше нормы`
        : null;

  const cards = [
    {
      key: "plan",
      title: "Плановая численность",
      value: formatNumber(summary.plannedHeadcount),
      subtitle: "По штатному плану",
      tone: "headcount",
    },
    {
      key: "fact",
      title: "Фактическая численность",
      value: formatNumber(summary.actualHeadcount),
      subtitle: "Активные сотрудники",
      tone: "headcount",
    },
    {
      key: "staffing",
      title: "Укомплектованность",
      value:
        summary.staffingPercent != null
          ? `${summary.staffingPercent}%`
          : "—",
      subtitle: "Факт / план",
      tone: staffingTone === "green" ? "green" : staffingTone,
      progress: summary.staffingPercent,
    },
    {
      key: "deficit",
      title: "Дефицит",
      value:
        summary.deficit != null ? formatNumber(summary.deficit) : "—",
      subtitle: "Не хватает сотрудников",
      tone: summary.deficitBadge?.tone || "neutral",
      badge: summary.deficitBadge?.text,
    },
    {
      key: "turnover",
      title: "Текучесть",
      value: formatPercent(summary.turnoverPercent),
      subtitle: "За выбранный период",
      tone: "orange",
      note: turnoverNote,
    },
  ];

  return (
    <div className="hr-dashboard__kpi-panel">
      <div className="hr-dashboard__kpi-grid">
        {cards.map((card) => (
          <article
            key={card.key}
            className={`hr-dashboard__kpi-card hr-dashboard__kpi-card--${card.tone}`}
          >
            <p className="hr-dashboard__kpi-title">{card.title}</p>
            <p className="hr-dashboard__kpi-value">{card.value}</p>
            <p className="hr-dashboard__kpi-subtitle">{card.subtitle}</p>
            {card.badge && (
              <span className={`hr-dashboard__badge hr-dashboard__badge--${card.tone}`}>
                {card.badge}
              </span>
            )}
            {card.note && (
              <span className="hr-dashboard__kpi-note">{card.note}</span>
            )}
            {card.progress != null && (
              <div className="hr-dashboard__kpi-progress">
                <div
                  className={`hr-dashboard__kpi-progress-fill hr-dashboard__kpi-progress-fill--${getStaffingColor(card.progress)}`}
                  style={{ width: `${Math.min(card.progress, 100)}%` }}
                />
              </div>
            )}
          </article>
        ))}
        {gender && <GenderKpiCard gender={gender} />}
      </div>
    </div>
  );
}

export default KpiCards;
